/**
 * Workspace FRAME components.
 *
 * The frame is fixed and identical for every seat: the rails, the buckets and
 * what qualifies for each, the action vocabulary, the source tag, the evidence
 * strip, the copilot position. These are contracts — a user learns them once,
 * so changing one retrains everybody. They change by decision, never by
 * generation.
 *
 * THE ROW IS ONE LINE
 * ───────────────────
 * Title, reference, figure, chevron. All reasoning lives behind the expand.
 * Reasoning rendered inline for every item fills a viewport with six rows and
 * nothing is scannable; the summary has to be readable at a glance and the
 * working has to be one click away, not zero.
 *
 * ACCENT IS SPENT, NOT SPRAYED
 * ────────────────────────────
 * Only "your court" carries the left accent. De-emphasised buckets drop their
 * TITLE to muted rather than putting opacity over the whole row — stacking
 * opacity on an already-muted token lands near 3:1 and fails AA for normal
 * text. Recession without illegibility.
 *
 * NO COMPLETION CLAIMS
 * ────────────────────
 * There is no component here that renders a status, an ETA or a percent
 * complete, and `Evidence` has no field to hold one. Enforcement is the
 * absence of an affordance rather than a rule someone has to remember, which
 * is the only kind that survives four more resolvers.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { extractBalancedJson } from '../chat/MessageContent'

/**
 * Coverage: one block per job, filled where recent production evidence exists.
 *
 * A list rather than a percentage on purpose. An order ships when its LAST job
 * is ready, so "4 of 11" is the finding — an aggregate would hide which ones
 * and read as progress, which is the one thing this must never do.
 */
export function Coverage({ evidence, className = '' }) {
  if (!evidence?.per_job?.length) return null
  const { per_job: perJob, jobs_with_evidence: withEvidence, jobs_total: total } = evidence

  return (
    <span
      className={`inline-flex gap-[2px] align-middle ${className}`}
      role="img"
      aria-label={`${withEvidence} of ${total} jobs have recent production evidence`}
      title={`${withEvidence} of ${total} jobs have recent production evidence`}
    >
      {perJob.map((hasEvidence, i) => (
        <i
          key={i}
          className="block w-2 h-2"
          style={{ background: hasEvidence ? 'var(--ws-cov-on)' : 'var(--ws-cov-off)' }}
        />
      ))}
    </span>
  )
}

/** The evidence strip: coverage, staleness, absence. Never completion. */
export function EvidenceStrip({ evidence }) {
  if (!evidence) return null
  const {
    jobs_with_evidence: withEvidence,
    jobs_total: total,
    freshest_days: freshest,
    due_dates_passed: passed,
    subcontract_jobs: subcontract,
    vendor_led_jobs: vendorLed,
  } = evidence

  const cell = (label, value, warn = false, extra = null) => (
    <div>
      <span className="block text-[11px] mb-0.5" style={{ color: 'var(--ws-mut)' }}>
        {label}
      </span>
      <b
        className="ws-num text-[13px] font-semibold"
        style={{ color: warn ? 'var(--ws-amber)' : 'var(--ws-tx)' }}
      >
        {value}
      </b>
      {extra}
    </div>
  )

  return (
    <div
      className="flex flex-wrap gap-5 mt-3 px-3 py-2 text-xs"
      style={{ background: 'var(--ws-panel)', border: '1px solid var(--ws-line-2)' }}
    >
      {total > 0 && cell(
        'Jobs with evidence',
        `${withEvidence} of ${total}`,
        withEvidence < total,
        <Coverage evidence={evidence} className="mt-1" />,
      )}
      {freshest != null && cell('Freshest posting', `${freshest} days`, freshest > 14)}
      {total > 0 && cell('Due dates passed', `${passed} of ${total}`, passed > 0)}
      {/* The caveat is specific, not blanket. Saying "some of these are
          subcontract" on every order teaches the reader to discount every
          finding; naming only the jobs whose staleness is genuinely
          uninformative leaves the rest of the signal intact. */}
      {vendorLed > 0 ? (
        <div className="basis-full text-[11px]" style={{ color: 'var(--ws-mut)' }}>
          {vendorLed} of {total} jobs are majority outsourced — their in-house
          labor can be finished and old while the vendor is still building, so
          their age is excluded from the figure above.
        </div>
      ) : subcontract > 0 ? (
        <div className="basis-full text-[11px]" style={{ color: 'var(--ws-mut)' }}>
          {subcontract} of {total} jobs have some outsourced work, but none is
          majority outsourced — in-house labor still tracks their progress.
        </div>
      ) : null}
    </div>
  )
}

/**
 * Where an item came from.
 *
 * Load-bearing for trust, not decoration: when an item is wrong you need to see
 * whether it misread the mail, misread Epicor, or joined the two incorrectly —
 * three different bugs. It sits inside the expand now that rows are one line,
 * which is a deliberate trade of glanceability for scannability.
 */
export function SourceTag({ label, pattern }) {
  const isJoin = pattern === 'join'
  return (
    <span
      className="inline-block mt-2 px-2 py-1 text-[11px] leading-none"
      style={{
        color: isJoin ? 'var(--ws-blue)' : 'var(--ws-mut)',
        border: `1px solid ${isJoin ? 'var(--ws-blue)' : 'var(--ws-line)'}`,
      }}
      title={isJoin ? 'Derived by joining more than one system' : 'From a single system'}
    >
      {label}
    </span>
  )
}

/**
 * One action on a row.
 *
 * Three states, and the third is the point. An action with an `href` is a real
 * link and opens in a new tab. An action the backend marks `enabled: false` is
 * visibly inert and says WHY on hover — Phase 0 defines more of the action
 * vocabulary than it can service yet, and a button that silently does nothing
 * reads as a broken product rather than an unbuilt one, while costing a click
 * to discover. Anything else behaves as a normal button.
 */
function Action({ action, onAct, item }) {
  const base = 'px-3 py-1.5 text-xs transition-colors'
  const style = action.primary
    ? { background: 'var(--ws-tx)', color: 'var(--ws-bg)', border: '1px solid var(--ws-tx)', fontWeight: 600 }
    : { background: 'transparent', color: 'var(--ws-tx-2)', border: '1px solid var(--ws-line)' }

  if (action.href) {
    return (
      <a
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`${base} no-underline inline-block`}
        style={style}
      >
        {action.label}
      </a>
    )
  }

  if (action.enabled === false) {
    return (
      <button
        type="button"
        disabled
        title={action.unavailable_because || 'Not available yet'}
        aria-label={`${action.label} — ${action.unavailable_because || 'not available yet'}`}
        className={`${base} cursor-not-allowed`}
        style={{
          background: 'transparent',
          color: 'var(--ws-mut)',
          border: '1px dashed var(--ws-line)',
          opacity: 0.65,
        }}
      >
        {action.label}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onAct?.(action.key, item) }}
      className={base}
      style={style}
    >
      {action.label}
    </button>
  )
}

/**
 * The reasoning, one cited clause at a time.
 *
 * Not a prose paragraph. Each sentence names the claim that licenses it and
 * whether it is an assertion, evidence, or a report of absence — which is what
 * makes "no completion claims" enforceable rather than a convention. An
 * absence clause is marked, because "nothing has been posted" is a different
 * kind of statement from "this was posted 28 days ago" and reading them the
 * same way is how an absence becomes a conclusion.
 *
 * `sampleText` is frame-preview copy and renders in a visibly different voice.
 * It carries no citation because it is not a finding.
 */
function Clauses({ clauses, sampleText }) {
  if (!clauses?.length) {
    return sampleText
      ? <p className="text-[13px] italic max-w-[74ch]" style={{ color: 'var(--ws-mut)' }}>{sampleText}</p>
      : null
  }

  return (
    <ul className="space-y-1.5 max-w-[74ch]">
      {clauses.map((c, i) => (
        <li key={i} className="flex gap-2 text-[13px]" style={{ color: 'var(--ws-tx-2)' }}>
          <span
            className="shrink-0 mt-[7px] w-1 h-1 rounded-full"
            style={{
              background: c.kind === 'absence' ? 'var(--ws-amber)' : 'var(--ws-mut)',
            }}
            aria-hidden="true"
          />
          <span>
            {c.text}{' '}
            <span
              className="text-[11px] whitespace-nowrap"
              style={{ color: 'var(--ws-mut)' }}
              title={`${c.kind} · ${c.authority} · ${c.source}`}
            >
              [{c.kind === 'absence' ? 'absence' : c.authority}]
            </span>
          </span>
        </li>
      ))}
    </ul>
  )
}

/** One row. Collapsed by default; the whole line is the toggle. */
export function Row({ item, tone = 'plain', onAct, draft }) {
  const [open, setOpen] = useState(false)
  const hot = tone === 'hot'
  const quiet = tone === 'quiet'

  return (
    <div style={{ borderBottom: '1px solid var(--ws-line-2)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 py-2.5 pl-3 pr-1 text-left"
        style={{
          borderLeft: `2px solid ${hot ? 'var(--ws-amber)' : 'transparent'}`,
          background: 'transparent',
        }}
      >
        {open
          ? <ChevronDown size={12} style={{ color: 'var(--ws-mut)' }} className="shrink-0" />
          : <ChevronRight size={12} style={{ color: 'var(--ws-mut)' }} className="shrink-0" />}

        <span
          className="flex-1 min-w-0 truncate text-sm"
          style={{
            color: quiet ? 'var(--ws-mut)' : 'var(--ws-tx)',
            fontWeight: hot ? 550 : 400,
          }}
        >
          {item.title}
        </span>

        {item.evidence?.per_job?.length > 0 && (
          <Coverage evidence={item.evidence} className="shrink-0" />
        )}

        {item.placeholder && (
          <span
            className="shrink-0 px-1.5 py-0.5 text-[10px] uppercase tracking-wide"
            style={{ color: 'var(--ws-amber)', border: '1px solid var(--ws-amber-dim)' }}
          >
            Sample
          </span>
        )}

        {item.meta && (
          <span className="ws-num shrink-0 text-xs whitespace-nowrap" style={{ color: 'var(--ws-mut)' }}>
            {item.meta}
          </span>
        )}
        {item.figure && (
          <span
            className="ws-num shrink-0 text-xs whitespace-nowrap text-right min-w-[64px]"
            style={{ color: 'var(--ws-tx-2)' }}
          >
            {item.figure}
          </span>
        )}
      </button>

      {open && (
        <div className="pl-8 pr-4 pb-4 pt-0.5">
          <Clauses clauses={item.clauses} sampleText={item.sample_text} />
          <EvidenceStrip evidence={item.evidence} />
          <div className="flex flex-wrap gap-2">
            {(item.sources || []).map((s, i) => (
              <SourceTag key={i} label={s.label} pattern={s.pattern} />
            ))}
          </div>
          {/* Why this item is filed where it is. Shown because the topic claim
              is indicative: a label nobody can check mis-files things forever,
              and the reader is the only one who can catch it. */}
          {item.domain_terms?.length > 0 && (
            <p className="mt-2 text-[11px]" style={{ color: 'var(--ws-mut)' }}>
              Filed here because the thread says{' '}
              {item.domain_terms.slice(0, 5).map((t) => `“${t}”`).join(', ')}.
            </p>
          )}
          {(item.actions || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {item.actions.map((a) => (
                <Action key={a.key} action={a} onAct={onAct} item={item} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * A bucket: one muted line carrying the name, the count and the inclusion test,
 * then its rows.
 *
 * Compressed to a single line rather than a heading plus a subtitle. The test
 * stays visible permanently because it is how a user checks our reasoning —
 * and the items are increasingly inferences ("nothing has moved and the date
 * has passed"), where an unstated test makes an inference an assertion.
 *
 * Returns null when empty. An absent bucket is information: no "waiting on
 * others" section means nothing is waiting. An empty state would destroy that
 * signal while looking friendlier.
 */
export function Bucket({ bucket, onAct, drafts = {} }) {
  if (!bucket?.items?.length) return null
  // Tone is derived from the bucket, as before — restoring the original rule
  // rather than inventing one: your court reads hot, waiting and closed read
  // quiet, everything else plain.
  const tone = bucket.key === 'your_court'
    ? 'hot'
    : (bucket.key === 'waiting_on_others' || bucket.key === 'closed_today') ? 'quiet' : 'plain'
  // Section only when sectioning says something. One domain across the whole
  // bucket means every heading would read the same, which is noise wearing
  // structure's clothes.
  const groups = (bucket.groups || []).length > 1 ? bucket.groups : null

  return (
    <section className="mb-8">
      <div
        className="flex items-baseline gap-2 pb-1.5 mb-1 text-xs"
        style={{ borderBottom: '1px solid var(--ws-line-2)' }}
      >
        <span className="font-semibold" style={{ color: 'var(--ws-tx)' }}>{bucket.title}</span>
        {/* The filtered length, not the server's count — dismissing a row and
            leaving the header saying 4 is the same bug as showing the row. */}
        <span className="ws-num" style={{ color: 'var(--ws-mut)' }}>{bucket.items.length}</span>
        <span className="truncate" style={{ color: 'var(--ws-mut)' }}>{bucket.test}</span>
      </div>
      {groups
        ? groups.map((g) => (
            <div key={g.key} className="mb-3">
              <p
                className="px-1 pt-3 pb-1 text-[11px] uppercase tracking-wider"
                style={{ color: 'var(--ws-mut)', borderBottom: '1px solid var(--ws-line-2)' }}
              >
                {g.title} <span className="ws-num">({g.count})</span>
              </p>
              {g.items.map((item) => (
                <Row key={item.id} item={item} tone={tone} onAct={onAct} draft={drafts[item.id]} />
              ))}
            </div>
          ))
        : bucket.items.map((item) => (
            <Row key={item.id} item={item} tone={tone} onAct={onAct} draft={drafts[item.id]} />
          ))}
    </section>
  )
}

/**
 * The composed region — one variable slot ABOVE the content.
 *
 * Renders nothing in Phase 0. It exists so two permanent rules are enforced by
 * the component tree rather than by memory: a generated element never occupies
 * a frame element's position, and anything the composed layer surfaces must
 * also be reachable through the frame.
 */
export function ComposedSlot({ composed }) {
  if (!composed) return null
  return (
    <div
      className="mb-6 p-4"
      style={{ border: '1px solid var(--ws-tx)', background: 'var(--ws-panel-2)' }}
    >
      {composed.kicker && (
        <p className="text-xs" style={{ color: 'var(--ws-mut)' }}>{composed.kicker}</p>
      )}
      <h2 className="mt-1 text-base font-semibold" style={{ color: 'var(--ws-tx)' }}>
        {composed.headline}
      </h2>
    </div>
  )
}

/**
 * What this page could NOT see.
 *
 * A capped book or an unreachable system means findings exist that are not
 * shown. Rendered above the content, because an empty bucket read after the
 * fact as "a quiet day" is the single most expensive misreading this surface
 * can produce — and the buckets themselves are deliberately silent when empty.
 */
export function Notes({ notes }) {
  if (!notes?.length) return null
  return (
    <div
      className="mb-6 px-3 py-2 text-[12px]"
      style={{
        border: '1px solid var(--ws-amber-dim)',
        background: 'var(--ws-panel)',
        color: 'var(--ws-tx-2)',
      }}
      role="status"
    >
      {notes.map((n, i) => (
        <p key={i} className={i ? 'mt-1' : ''}>{n}</p>
      ))}
    </div>
  )
}

/** Says plainly that this page is not showing the user's data yet. */
export function PreviewNotice() {
  return (
    <div
      role="status"
      className="mb-6 flex items-start gap-3 p-3 text-[13px]"
      style={{
        border: '1px solid var(--ws-amber-dim)',
        background: 'var(--ws-panel)',
        color: 'var(--ws-tx-2)',
      }}
    >
      <AlertCircle size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--ws-amber)' }} />
      <p>
        <strong className="font-semibold" style={{ color: 'var(--ws-tx)' }}>Frame preview.</strong>{' '}
        Every item below is sample content used to review the layout — none of it
        is drawn from your orders or your mail. The content layer that produces
        real items is not built yet.
      </p>
    </div>
  )
}

/**
 * A source this user could connect and has not.
 *
 * Deliberately not styled as an upsell. The tenant already has this connector;
 * the page is simply running on half its sources, and the person reading it is
 * the one who can fix that. So it states what the page CANNOT SEE — the same
 * absence-versus-incompleteness distinction every item on this surface rests on
 * — and puts the action next to it.
 *
 * It sits ABOVE the content, because a page missing half its sources should not
 * be read as a finished answer while the reason scrolls below the fold.
 */
export function SetupGaps({ setup }) {
  if (!setup?.length) return null
  return (
    <>
      {setup.map((gap) => (
        <div
          key={gap.key}
          role="status"
          className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 text-[13px]"
          style={{
            border: '1px solid var(--ws-amber-dim)',
            background: 'var(--ws-panel)',
            color: 'var(--ws-tx-2)',
          }}
        >
          <span className="flex-1 min-w-[16rem]">{gap.why}</span>
          <a
            href={gap.href}
            className="shrink-0 px-3 py-1.5 text-[12px] font-semibold no-underline"
            style={{
              border: '1px solid var(--ws-amber)',
              color: 'var(--ws-tx)',
              background: 'var(--ws-panel-2)',
            }}
          >
            Connect {gap.label}
          </a>
        </div>
      ))}
    </>
  )
}

/**
 * A draft, once Trendy has written one.
 *
 * Shown in full rather than summarised: it goes out under the user's name, so
 * they read every word before it does. The model that wrote it and the reason
 * that model was chosen are stated, because an invisible cost decision made on
 * someone's behalf is still a decision made on their behalf.
 *
 * Routing suggestions are listed with their reason and are NOT applied. The
 * mail port takes a reply's recipients from Graph, so adding somebody is an act
 * the user performs in Outlook — a recipient this panel had quietly added would
 * be one click from disclosing a thread to a person never on it.
 */
export function DraftPanel({ draft }) {
  if (!draft) return null

  if (draft.status === 'working') {
    return (
      <p className="mt-3 text-[12px]" style={{ color: 'var(--ws-mut)' }}>
        Drafting from this item's cited facts…
      </p>
    )
  }

  if (draft.status === 'error') {
    return (
      <p className="mt-3 text-[12px]" style={{ color: 'var(--ws-red)' }}>
        {draft.message}
      </p>
    )
  }

  // The refusal case. Nothing was saved, and the reason is the finding.
  if (draft.created === false && draft.reason) {
    return (
      <div className="mt-3 px-3 py-2 text-[12px]"
           style={{ border: '1px solid var(--ws-amber)', background: 'var(--ws-panel)', color: 'var(--ws-tx-2)' }}>
        <b style={{ color: 'var(--ws-amber)' }}>Draft refused.</b> {draft.reason}
        {draft.completion_language?.length > 0 && (
          <span> It said: {draft.completion_language.map((p) => `“${p}”`).join(', ')}.</span>
        )}
      </div>
    )
  }

  return (
    <div className="mt-3 px-3 py-3 text-[13px]"
         style={{ border: '1px solid var(--ws-line)', background: 'var(--ws-panel)' }}>
      <p className="whitespace-pre-wrap" style={{ color: 'var(--ws-tx)' }}>{draft.body}</p>

      {draft.suggested_recipients?.length > 0 && (
        <div className="mt-3 pt-2" style={{ borderTop: '1px solid var(--ws-line-2)' }}>
          <p className="text-[11px] mb-1" style={{ color: 'var(--ws-mut)' }}>
            Also relevant — add them yourself in Outlook:
          </p>
          {draft.suggested_recipients.map((r) => (
            <p key={r.address} className="text-[12px]" style={{ color: 'var(--ws-tx-2)' }}>
              {r.address} <span style={{ color: 'var(--ws-mut)' }}>— {r.reason}</span>
            </p>
          ))}
        </div>
      )}

      {/* Saved or not is the difference between "go and send it" and "copy
          this". Stated plainly rather than as a grey aside, with the reason
          when Outlook refused it. */}
      {draft.created ? (
        <p className="mt-3 text-[11px]" style={{ color: 'var(--ws-mut)' }}>
          Saved to your Outlook drafts, unsent. Written by {draft.model} — {draft.model_reason}.
        </p>
      ) : (
        <div className="mt-3 px-2.5 py-2 text-[11.5px]"
             style={{ border: '1px solid var(--ws-amber-dim)', color: 'var(--ws-tx-2)' }}>
          <b style={{ color: 'var(--ws-amber)' }}>Not saved to Outlook.</b>{' '}
          Copy the text above. {draft.save_error ? `Outlook said: ${draft.save_error}` : ''}
          <span style={{ color: 'var(--ws-mut)' }}> Written by {draft.model}.</span>
        </div>
      )}
      {draft.draft_url && (
        <a href={draft.draft_url} target="_blank" rel="noopener noreferrer"
           className="inline-block mt-2 px-3 py-1.5 text-xs no-underline"
           style={{ border: '1px solid var(--ws-tx)', color: 'var(--ws-tx)' }}>
          Open the draft
        </a>
      )}
    </div>
  )
}

/**
 * Asking, in the rail.
 *
 * Deliberately NOT a second chat application. It posts to the same /api/chat
 * the main app uses, so it reaches the same 388 tools and the same
 * anti-fabrication guards — extracting the streaming UI out of ChatApp is real
 * work, and none of it was needed to make the box answer a question.
 *
 * WHAT IT CARRIES THAT THE MAIN CHAT CANNOT
 * The record you were looking at. "Has anything moved on this?" resolves
 * against the row you had open, rather than needing an order number retyped
 * from a screen that is already showing it. The context is sent labelled, never
 * merged into the message, so a wrong record cannot masquerade as the user's
 * own words.
 *
 * WHAT IT IS HONEST ABOUT
 * Which tools answered. This path is the ROUTER — it returns rows and the model
 * says what they mean, with no claim citation behind it. Naming the tools is
 * the weakest honest thing available until the claim layer moves to the
 * platform, and it is considerably better than a confident paragraph from
 * nowhere.
 */
/**
 * Answers in the rail, rendered rather than printed.
 *
 * The rail was putting the model's reply on the page as plain text, so markdown
 * arrived literally: asterisks around the bold, a `#` in front of every
 * heading, and tables as rows of pipe characters. The chat path can also emit
 * an inline visual as a JSON payload for the main application to draw. The rail
 * has no drawing surface, so that payload leaked into the conversation as a
 * block of raw JSON sitting in the middle of the answer.
 *
 * Two things follow. Strip what the rail cannot draw, and render the rest with
 * a type scale built for a 330px column — react-markdown's defaults inherit the
 * surrounding page, which next to 12.5px body text means headings the size of
 * the page title and tables that run off the edge.
 */
const RAIL_MD_PLUGINS = [remarkGfm]

/**
 * Remove `{"type":"inline", …}` visual payloads and any fence around them.
 *
 * Shares MessageContent's balanced-brace reader rather than re-deriving it: a
 * second parser that drifts from the first would strip these in one place and
 * not the other, which is how a raw payload reaches a reader in the first place.
 */
export function stripInlineVisuals(text) {
  if (!text || !text.includes('"inline"')) return text

  const finder = /\{\s*"type"\s*:\s*"inline"/g
  const cuts = []
  let hit
  while ((hit = finder.exec(text)) !== null) {
    const json = extractBalancedJson(text, hit.index)
    if (!json) continue

    let start = hit.index
    let end = hit.index + json.length

    const before = text.substring(Math.max(0, hit.index - 30), hit.index)
    const opening = before.match(/```(?:json)?\s*\n?\s*$/)
    if (opening) start -= opening[0].length

    const after = text.substring(end, Math.min(text.length, end + 20))
    const closing = after.match(/^\s*\n?\s*```/)
    if (closing) end += closing[0].length

    cuts.push([start, end])
  }
  if (!cuts.length) return text

  let out = ''
  let cursor = 0
  for (const [start, end] of cuts) {
    out += text.slice(cursor, start)
    cursor = end
  }
  out += text.slice(cursor)

  // Removing a block mid-answer leaves the blank lines that surrounded it.
  return out.replace(/\n{3,}/g, '\n\n').trim()
}

const RAIL_MD_COMPONENTS = {
  p: ({ children }) => (
    <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--ws-tx-2)' }}>{children}</p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold" style={{ color: 'var(--ws-tx)' }}>{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,

  // One heading weight. A rail this narrow cannot show three levels of
  // hierarchy without the smallest of them looking like an accident.
  h1: ({ children }) => (
    <p className="mt-3 first:mt-0 text-[13px] font-semibold" style={{ color: 'var(--ws-tx)' }}>{children}</p>
  ),
  h2: ({ children }) => (
    <p className="mt-3 first:mt-0 text-[13px] font-semibold" style={{ color: 'var(--ws-tx)' }}>{children}</p>
  ),
  h3: ({ children }) => (
    <p className="mt-3 first:mt-0 text-[12.5px] font-semibold" style={{ color: 'var(--ws-tx)' }}>{children}</p>
  ),
  h4: ({ children }) => (
    <p className="mt-3 first:mt-0 text-[12.5px] font-semibold" style={{ color: 'var(--ws-tx)' }}>{children}</p>
  ),

  // list-disc / list-decimal explicitly: the workspace stylesheet clears list
  // markers globally, and a bullet list with no bullets reads as loose lines.
  ul: ({ children }) => <ul className="my-1.5 pl-4 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 pl-4 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => (
    <li className="text-[12.5px] leading-relaxed" style={{ color: 'var(--ws-tx-2)' }}>{children}</li>
  ),

  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2"
       style={{ color: 'var(--ws-blue)' }}>
      {children}
    </a>
  ),

  // A table in a 330px column will not fit and should not be squeezed until it
  // is unreadable. It scrolls sideways inside its own box instead.
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto" style={{ border: '1px solid var(--ws-line-2)' }}>
      <table className="w-full text-[11px] border-collapse">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="px-2 py-1 text-left font-semibold whitespace-nowrap"
        style={{ color: 'var(--ws-tx)', borderBottom: '1px solid var(--ws-line-2)' }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2 py-1 align-top" style={{ color: 'var(--ws-tx-2)' }}>{children}</td>
  ),

  code: ({ children }) => (
    <code className="ws-num text-[11px] px-1" style={{ background: 'var(--ws-panel-2)' }}>{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="my-2 p-2 text-[11px] overflow-x-auto"
         style={{ background: 'var(--ws-panel-2)', border: '1px solid var(--ws-line-2)' }}>
      {children}
    </pre>
  ),

  blockquote: ({ children }) => (
    <blockquote className="my-2 pl-2.5" style={{ borderLeft: '2px solid var(--ws-line)' }}>{children}</blockquote>
  ),
  hr: () => <hr className="my-3" style={{ border: 0, borderTop: '1px solid var(--ws-line-2)' }} />,
}

function RailMarkdown({ text }) {
  const cleaned = useMemo(() => stripInlineVisuals(text || ''), [text])
  if (!cleaned) return null
  return (
    <div className="grid gap-1.5">
      <ReactMarkdown remarkPlugins={RAIL_MD_PLUGINS} components={RAIL_MD_COMPONENTS}>
        {cleaned}
      </ReactMarkdown>
    </div>
  )
}

export function RailChat({ context }) {
  const [turns, setTurns] = useState([])
  const [draftText, setDraftText] = useState('')
  const [busy, setBusy] = useState(false)
  const endRef = useRef(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [turns, busy])

  const send = async (e) => {
    e?.preventDefault?.()
    const asked = draftText.trim()
    if (!asked || busy) return

    const history = turns.map((t) => ({ role: t.role, content: t.content }))
    setTurns((t) => [...t, { role: 'user', content: asked }])
    setDraftText('')
    setBusy(true)
    try {
      const token = localStorage.getItem('amtrend_token')
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: asked, history, context }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.detail || `Failed (${res.status})`)
      setTurns((t) => [...t, {
        role: 'assistant',
        content: data.response || data.message || '(no answer)',
        tools: data.tools_used || [],
      }])
    } catch (err) {
      setTurns((t) => [...t, { role: 'error', content: err.message }])
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto">
        {turns.length === 0 && (
          <p className="text-[12.5px]" style={{ color: 'var(--ws-mut)' }}>
            Ask about this record or anything else in Epicor, your mail, materials
            or cash. Answers name the tools they came from.
          </p>
        )}

        <div className="grid gap-3">
          {turns.map((t, i) => {
            if (t.role === 'user') {
              return (
                <p key={i} className="text-[12.5px] px-2.5 py-1.5 justify-self-end max-w-[92%]"
                   style={{ background: 'var(--ws-panel-2)', color: 'var(--ws-tx)', border: '1px solid var(--ws-line-2)' }}>
                  {t.content}
                </p>
              )
            }
            if (t.role === 'error') {
              return (
                <p key={i} className="text-[12px]" style={{ color: 'var(--ws-red)' }}>
                  {t.content}
                </p>
              )
            }
            return (
              <div key={i}>
                <RailMarkdown text={t.content} />
                {/* Where it looked. This path returns rows rather than claims,
                    so naming the tools is the honest floor. */}
                {t.tools?.length > 0 && (
                  <p className="mt-1.5 text-[10.5px]" style={{ color: 'var(--ws-mut)' }}>
                    read: {t.tools.slice(0, 6).join(', ')}
                    {t.tools.length > 6 ? ` +${t.tools.length - 6} more` : ''}
                  </p>
                )}
              </div>
            )
          })}
        </div>

        {busy && (
          <p className="mt-3 text-[12px]" style={{ color: 'var(--ws-mut)' }}>Looking…</p>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="pt-3 shrink-0">
        <input
          id="ws-ask"
          type="text"
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          disabled={busy}
          placeholder="Ask about orders, mail, materials or cash"
          aria-label="Ask Trendy"
          className="w-full px-3 py-2 text-[13px]"
          style={{ background: 'var(--ws-bg)', color: 'var(--ws-tx)', border: '1px solid var(--ws-line)' }}
        />
      </form>
    </div>
  )
}
